/**
 * LegalScreen — renders the app's Privacy Policy and Terms of Service in-app.
 * The markdown is the same source mirrored to docs/legal/ for public hosting.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Browser } from '@capacitor/browser';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { MarkdownContent } from '../components/MarkdownContent';
import { notify } from '../utils/notifications';
import privacyPolicy from '../content/legal/privacy-policy.md?raw';
import termsOfService from '../content/legal/terms-of-service.md?raw';
import { PUBLIC_RESOURCE_URLS } from '../config/publicResources';

type Doc = 'privacy' | 'terms';

const CANONICAL_URLS: Record<Doc, string> = {
  privacy: PUBLIC_RESOURCE_URLS.privacy,
  terms: PUBLIC_RESOURCE_URLS.terms,
};

export default function LegalScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial: Doc = params.get('doc') === 'terms' ? 'terms' : 'privacy';
  const [doc, setDoc] = useState<Doc>(initial);

  const select = (next: Doc) => {
    setDoc(next);
    setParams({ doc: next }, { replace: true });
  };

  const content = doc === 'privacy' ? privacyPolicy : termsOfService;
  const openWebVersion = async () => {
    try {
      await Browser.open({ url: CANONICAL_URLS[doc] });
    } catch {
      notify.error('Could not open the web version', 'Please try again when you are online.');
    }
  };

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div
        className="app-screen-header flex items-center gap-3 px-4 pt-12 pb-3 shrink-0 border-b"
        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-divider)' }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <span className="text-base" style={{ color: 'var(--app-text)' }}>Legal</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 py-3 shrink-0 border-b" style={{ borderColor: 'var(--app-divider)' }}>
        {([['privacy', 'Privacy Policy'], ['terms', 'Terms of Service']] as [Doc, string][]).map(
          ([id, label]) => (
            <button
              key={id}
              onClick={() => select(id)}
              className="px-3 py-1.5 rounded-full text-xs transition-colors"
              style={
                doc === id
                  ? { background: 'var(--app-accent-solid)', color: '#fff' }
                  : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)', border: '1px solid var(--app-border)' }
              }
            >
              {label}
            </button>
          ),
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-16">
        <button
          onClick={openWebVersion}
          className="w-full mb-4 rounded-xl border px-4 py-3 flex items-center justify-between gap-3 text-left active:opacity-70"
          style={{
            backgroundColor: 'var(--app-surface)',
            borderColor: 'var(--app-border)',
            color: 'var(--app-accent)',
          }}
        >
          <span className="text-sm">View web version</span>
          <ExternalLink className="w-4 h-4 shrink-0" aria-hidden="true" />
        </button>
        <MarkdownContent content={content} />
      </div>
    </div>
  );
}
